import { createComponentStore } from '../../utils/createComponentStore';
import uuid from 'uuid';
import { getColourFromString } from '../../utils/colorUtils';

// ── Utility helpers (module-scope) ──────────────────────────────────────────

const keyAuto = () => 'conditional' + (Math.floor(Math.random() * 90000000) + 10000).toString();

const generatePathName = () => 'path' + (Math.floor(Math.random() * 90000000) + 10000).toString();

const logicData = () => ({
  key: keyAuto(),
  comPId: uuid(),
  pathName: generatePathName(),
  color: getColourFromString(),
  outputType: 'TEXT',
  statement: {
    type: 'IF',
    content: {
      condition: [
        {
          operators: [
            { logic: 'undefined', key: '', function: 'EQUAL', value: '' },
          ],
          then: { statement: { type: 'VALUE', content: 'CONTINUE' } },
        },
        {
          then: { statement: { type: 'VALUE', content: 'STOP' } },
        },
      ],
    },
  },
});

// ── Store data shape ────────────────────────────────────────────────────────

const initData = () => [logicData()];

// Data is an array — return rawData directly instead of spreading over initData()
const onLoadData = (rawData) => (Array.isArray(rawData) ? rawData : initData());

// ── Normalise loaded data items (add default statement when missing) ────────

const normaliseLogicItems = (items) =>
  items.map((dat) => {
    if (!dat.hasOwnProperty('statement')) {
      return {
        ...dat,
        color: getColourFromString(),
        statement: {
          type: 'IF',
          content: {
            condition: [
              {
                operators: [
                  { logic: 'undefined', key: '', function: 'EQUAL', value: '' },
                ],
                then: { statement: { type: 'VALUE', content: 'CONTINUE' } },
              },
              {
                then: { statement: { type: 'VALUE', content: 'STOP' } },
              },
            ],
          },
        },
      };
    }
    return dat;
  });

// ── Create store via factory ────────────────────────────────────────────────

const { getStore, defaultStore } = createComponentStore({
  type: 'conditional_workflow_path',
  endpoint: 'conditional-workflow-path',
  registryKey: 'conditional-workflow-path',
  initData,
  onLoadData,

  extraState: () => ({
    placeholders: [],
    positions: {
      indexLogic: null,
      indexStatement: null,
      indexCondition: null,
      type: null,
    },
  }),

  extraActions: (set, get) => ({
    // ── Backward-compat alias: old template setter ────────────────────────
    setConditionalWorkflowTemplate: (template) => {
      const data = normaliseLogicItems(template.data);
      set({
        type: template.type,
        name: template.name,
        data,
        lastModified: template.lastModified,
      });
    },

    // ── Backward-compat alias for name setter ─────────────────────────────
    setConditionalWorkflowName: (name) => set({ name }),

    // ── Condition / operator editing ──────────────────────────────────────
    setCondition: (content, indexLogic, indexStatement, indexCondition, type) => {
      const data = JSON.parse(JSON.stringify(get().data));
      switch (type) {
        case 'logic':
          data[indexLogic].statement.content.condition[indexStatement].operators[indexCondition].logic = content;
          break;
        case 'key':
          data[indexLogic].statement.content.condition[indexStatement].operators[indexCondition].key = content ? content.trim() : '';
          break;
        case 'pathName':
          data[indexLogic].statement.content.condition[indexStatement].operators[indexCondition].pathName = content ? content.trim() : '';
          break;
        case 'function':
          data[indexLogic].statement.content.condition[indexStatement].operators[indexCondition].function = content;
          break;
        case 'value':
          data[indexLogic].statement.content.condition[indexStatement].operators[indexCondition].value = content;
          break;
      }
      set({ data });
    },

    setThenContent: (content, indexLogic, indexStatement) => {
      const data = JSON.parse(JSON.stringify(get().data));
      data[indexLogic].statement.content.condition[indexStatement].then.statement.content = content;
      set({ data });
    },

    // ── Add / remove statements & conditions ──────────────────────────────
    addStatement: (indexLogic) => {
      const obj = {
        operators: [{ logic: 'OR', key: '', function: '', value: '' }],
        then: { statement: { type: 'VALUE', content: '' } },
      };
      const data = JSON.parse(JSON.stringify(get().data));
      const { condition } = data[indexLogic].statement.content;
      data[indexLogic].statement.content.condition.splice(condition.length - 1, 0, obj);
      set({ data });
    },

    addCondition: (indexLogic, indexStatement) => {
      const obj = { logic: 'OR', key: '', function: 'EQUAL', value: '' };
      const data = JSON.parse(JSON.stringify(get().data));
      data[indexLogic].statement.content.condition[indexStatement].operators.push(obj);
      set({ data });
    },

    removeCondition: (indexLogic, indexStatement, indexCondition) => {
      const data = JSON.parse(JSON.stringify(get().data));
      data[indexLogic].statement.content.condition[indexStatement].operators.splice(indexCondition, 1);
      set({ data });
    },

    removeStatement: (indexLogic, indexStatement) => {
      const data = JSON.parse(JSON.stringify(get().data));
      data[indexLogic].statement.content.condition.splice(indexStatement, 1);
      set({ data });
    },

    // ── Logic-level editing ───────────────────────────────────────────────
    setLogic: (content, index, type) => {
      const data = JSON.parse(JSON.stringify(get().data));
      switch (type) {
        case 'key':
          data[index].key = content;
          break;
        case 'outputType':
          data[index].outputType = content;
          break;
        case 'pathName':
          data[index].pathName = content;
          break;
      }
      set({ data });
    },

    addLogic: () => {
      const obj = logicData();
      set({ data: [...get().data, obj] });
    },

    removeLogic: (index) => {
      const data = [...get().data];
      data.splice(index, 1);
      set({ data });
    },

    copyThisLogic: (index) => {
      const data = JSON.parse(JSON.stringify(get().data));
      const copyLogic = {
        ...data[index],
        key: keyAuto(),
        comPId: uuid(),
        pathName: generatePathName(),
        color: getColourFromString(),
      };
      set({ data: [...data, copyLogic] });
    },

    // ── Placeholder / position helpers ────────────────────────────────────
    setPlaceholders: (placeholders) => set({ placeholders }),

    setPositions: (positions) => set({ placeholders: [], positions }),

    setData: (data) => set({ data }),

    // ── Expose logicData generator on the store for backward compat ───────
    logicData,

    // ── Override getTemplate to include placeholders & positions ──────────
    getTemplate: () => {
      const s = get();
      return {
        componentId: s.componentId,
        name: s.name,
        type: s.type,
        lastModified: s.lastModified,
        data: JSON.parse(JSON.stringify(s.data)),
        placeholders: s.placeholders,
        positions: s.positions,
      };
    },
  }),
});

// ── Exports ─────────────────────────────────────────────────────────────────

export function getConditionalWorkflowPathStore(componentId) {
  return getStore(componentId);
}

export { logicData, keyAuto, generatePathName };

export default defaultStore;
